import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'empowernow:pinned-plugins';
/** Legacy path-keyed favorites store previously written by the Home page. */
const LEGACY_FAVORITES_KEY = 'experience:favorites';
/** Idempotent one-time-migration marker (path -> plugin id unification). */
const MIGRATION_FLAG_KEY = 'empowernow:favorites-migrated:v1';

/**
 * One-time migration of the legacy path-keyed Home favorites into the canonical
 * plugin-id pin store, so unifying the two stores does not silently drop a
 * returning user's favorites.
 *
 * Idempotent by construction: it unions into the existing pin Set and is guarded
 * by a keyed flag, so a multi-tab race that runs it twice merges the same ids
 * (a no-op the second time) rather than corrupting state. Returns true when a
 * migration actually wrote new pins (so callers can refresh in-memory state).
 */
export function migrateLegacyFavorites(pathToPluginId: Map<string, string>): boolean {
  try {
    if (localStorage.getItem(MIGRATION_FLAG_KEY)) return false;

    const legacyRaw = localStorage.getItem(LEGACY_FAVORITES_KEY);
    let changed = false;

    if (legacyRaw) {
      const legacyPaths = JSON.parse(legacyRaw);
      if (Array.isArray(legacyPaths)) {
        const pins = loadPinsFromStorage();
        const before = pins.size;
        for (const path of legacyPaths) {
          if (typeof path !== 'string') continue;
          const pluginId = pathToPluginId.get(path);
          if (pluginId) pins.add(pluginId);
        }
        if (pins.size !== before) {
          savePinsToStorage(pins);
          changed = true;
        }
      }
      // Delete the legacy key regardless — old code path is retired.
      localStorage.removeItem(LEGACY_FAVORITES_KEY);
    }

    localStorage.setItem(MIGRATION_FLAG_KEY, new Date().toISOString());
    return changed;
  } catch {
    return false;
  }
}

export interface FavoritePluginsHook {
  pinnedIds: Set<string>;
  togglePin: (pluginId: string) => void;
  isPinned: (pluginId: string) => boolean;
  clearPins: () => void;
  /** Re-read the store into memory (used after a same-tab legacy migration). */
  refresh: () => void;
}

function loadPinsFromStorage(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed.filter((id): id is string => typeof id === 'string'));
      }
    }
  } catch {
    // Ignore storage errors
  }
  return new Set();
}

function savePinsToStorage(pins: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(pins)));
  } catch {
    // Ignore storage errors
  }
}

export function useFavoritePlugins(): FavoritePluginsHook {
  const [pinnedIds, setPinnedIds] = useState<Set<string>>(() => loadPinsFromStorage());

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue);
          if (Array.isArray(parsed)) {
            setPinnedIds(new Set(parsed.filter((id): id is string => typeof id === 'string')));
          }
        } catch {
          // Ignore
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const togglePin = useCallback((pluginId: string) => {
    setPinnedIds(prev => {
      const next = new Set(prev);
      if (next.has(pluginId)) {
        next.delete(pluginId);
      } else {
        next.add(pluginId);
      }
      savePinsToStorage(next);
      return next;
    });
  }, []);

  const isPinned = useCallback((pluginId: string) => {
    return pinnedIds.has(pluginId);
  }, [pinnedIds]);

  const clearPins = useCallback(() => {
    setPinnedIds(new Set());
    savePinsToStorage(new Set());
  }, []);

  const refresh = useCallback(() => {
    setPinnedIds(loadPinsFromStorage());
  }, []);

  return { pinnedIds, togglePin, isPinned, clearPins, refresh };
}
